import { useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import clsx from 'clsx'

export interface TooltipProps {
  text: string
  children: ReactNode
  className?: string
}

const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 8

// Fixed (out-of-flow) even while hidden - if left unset, the label would lay
// out in normal flow as a sibling of the trigger during this one-frame
// measurement pass, and its own ~80px height could stretch a `flex
// items-center` ancestor row, shifting where the trigger itself measures
// from before the layout effect below ever runs.
const HIDDEN_STYLE: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }

// Hover/focus-triggered explanatory label - PRD 00's own "never disabled
// with no explanation" rule assumes this exists, but PRD 00's actual output
// list (ChipInput/ConfidenceSlider/DataTable/StatusPill/EmptyState/
// ConfirmModal) never built it. PRD 02 is the first screen that needs it
// (hard-cap/lock/disabled-button affordances), so it's added here as a
// shared primitive for future PRDs to reuse rather than left feature-local.
//
// Positioned via `position: fixed` with JS-measured, viewport-clamped
// coordinates rather than `absolute` + CSS centering: an `absolute` label is
// clipped by any ancestor with non-visible overflow (e.g. DataTable's
// overflow-x-auto, which the CSS overflow spec forces to also clip
// vertically), and naive centering pushes the label off-screen for any
// trigger near a viewport edge (e.g. a table's rightmost column). `fixed`
// escapes both, since no ancestor here establishes a new containing block
// (transform/filter/will-change).
export function Tooltip({ text, children, className }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const tooltipId = useId()

  useLayoutEffect(() => {
    if (!visible) return
    const trigger = triggerRef.current
    const label = labelRef.current
    if (!trigger || !label) return

    const triggerRect = trigger.getBoundingClientRect()
    const labelRect = label.getBoundingClientRect()

    const centered = triggerRect.left + triggerRect.width / 2 - labelRect.width / 2
    const maxLeft = window.innerWidth - labelRect.width - VIEWPORT_MARGIN
    const left = Math.min(Math.max(centered, VIEWPORT_MARGIN), Math.max(maxLeft, VIEWPORT_MARGIN))

    const above = triggerRect.top - labelRect.height - TRIGGER_GAP
    const top = above < VIEWPORT_MARGIN ? triggerRect.bottom + TRIGGER_GAP : above

    setStyle({ position: 'fixed', top, left, visibility: 'visible' })
  }, [visible])

  return (
    <span
      ref={triggerRef}
      className={clsx('inline-flex', className)}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span aria-describedby={visible ? tooltipId : undefined}>{children}</span>
      {visible && (
        <span
          ref={labelRef}
          id={tooltipId}
          role="tooltip"
          style={style}
          className="z-50 w-max max-w-[240px] rounded-control border border-border bg-surface-raised px-3 py-2 text-meta text-text-primary shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  )
}
